// Bulk Import (CSV) tab — matches Figma design exactly
// Drop zone → template download card → CSV requirements → Cancel/Process Import footer
import React, { useState, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { readString } from 'react-papaparse';
import axios from 'axios';
import downloadFile from 'js-file-download';
import { FileText, Download, Send, X, CheckCircle } from 'lucide-react';
import { ErrorToast } from 'components';
import { DialogFooter } from 'components/ui/dialog';
import EmployeeService from 'api/employees';
import { cn } from 'lib/utils';

const S3_TEMPLATE_URL = 'https://bed-rock.s3.ap-southeast-1.amazonaws.com/CSV_templates/bedrock_member_upload.csv';
const MAX_RECORDS = 500;

// Validate and transform raw CSV rows into employee payload
function parseCsvRows(csvData) {
  let hasError = false;
  const rows = csvData.map((data) => {
    let rowError = false;
    if (!data['First Name'] && !data['FirstName']) {
      toast.error(<ErrorToast message="First name is required" />, { toastId: 'csv-fname' });
      rowError = true;
    }
    if (!data['Last Name'] && !data['LastName']) {
      toast.error(<ErrorToast message="Last name is required" />, { toastId: 'csv-lname' });
      rowError = true;
    }
    const email = data.Email || data.email;
    if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email)) {
      toast.error(<ErrorToast message={`Invalid email: ${email}`} />, { toastId: 'csv-email' });
      rowError = true;
    }
    if (rowError) { hasError = true; return null; }
    return {
      firstName: data['First Name'] || data['FirstName'] || '',
      lastName: data['Last Name'] || data['LastName'] || '',
      email,
      passNo: data['Passport No'] || '',
      idNo: data['National ID'] || '',
      idType: 'NID',
      designation: data['Designation'] || data.designation || '',
      departments: [data['Department'] || data.department || ''],
      action: data['Action'] || '',
      sendActivationEmail: (data['Send Activation Email'] || '').toLowerCase().trim() === 'true',
    };
  });
  return hasError ? [] : rows.filter(Boolean);
}

const CrewBulkImportTab = ({ fetchEmployees, onClose }) => {
  const fileInputRef = useRef(null);
  const [csvFile, setCsvFile] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  const hasStatusCol = employees.some((e) => !!e?.status);
  const hasErrorCol = employees.some((e) => e?.error?.trim());
  const hasActionCol = employees.some((e) => !!e?.action);

  const clearFile = () => {
    setCsvFile(null);
    setEmployees([]);
    setIsSaved(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFile = useCallback((file) => {
    if (!file || !file.name.endsWith('.csv')) {
      toast.error(<ErrorToast message="Please upload a .csv file" />);
      return;
    }
    setCsvFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      const results = readString(e.target.result, { header: true, skipEmptyLines: true });
      if (results.data.length > MAX_RECORDS) {
        toast.error(<ErrorToast message={`Max ${MAX_RECORDS} records per upload. File has ${results.data.length}.`} />);
        return;
      }
      const parsed = parseCsvRows(results.data);
      setEmployees(parsed);
    };
    reader.onerror = () => toast.error(<ErrorToast message="Failed to read CSV file" />);
    reader.readAsText(file);
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFile(e.dataTransfer.files?.[0]);
  }, [handleFile]);

  const downloadTemplate = async () => {
    try {
      setIsDownloading(true);
      const resp = await axios({ url: S3_TEMPLATE_URL, method: 'GET', responseType: 'blob' });
      downloadFile(resp.data, 'bedrock_member_invite.csv');
    } catch {
      toast.error(<ErrorToast message="Error downloading template" />);
    } finally {
      setIsDownloading(false);
    }
  };

  const processImport = async () => {
    if (!employees.length) return;
    try {
      setIsSaving(true);
      const clientId = localStorage.getItem('ROOT_ID');
      const payload = employees.map((emp) => ({ ...emp, clientId }));
      const result = await EmployeeService.bulkUpload(payload);
      const dataResult = result?.data?.data || [];
      const withStatus = payload.map((emp, i) => ({
        ...emp,
        status: dataResult[i]?.status || '',
        error: dataResult[i]?.error || '',
      }));
      setEmployees(withStatus);
      await fetchEmployees();
      setIsSaved(true);
    } catch (error) {
      toast.error(<ErrorToast error={error} />);
    } finally {
      setIsSaving(false);
    }
  };

  // Show preview table after CSV parsed, otherwise show upload form
  if (employees.length > 0) {
    return (
      <div className="tw-space-y-3">
        {/* File info bar */}
        <div className="tw-flex tw-items-center tw-justify-between">
          <span className="tw-text-xs tw-text-gray-500">
            {csvFile?.name} — {employees.length} record{employees.length !== 1 ? 's' : ''}
          </span>
          {!isSaved && (
            <button type="button" onClick={clearFile} className="tw-text-xs tw-text-red-500 tw-bg-transparent tw-border-0 tw-cursor-pointer hover:tw-underline tw-flex tw-items-center tw-gap-1">
              <X className="tw-w-3 tw-h-3" /> Remove
            </button>
          )}
        </div>
        {/* Preview table */}
        <div className="tw-max-h-[300px] tw-overflow-auto tw-border tw-border-gray-200 tw-rounded-lg">
          <table className="tw-w-full tw-text-xs">
            <thead className="tw-bg-gray-50 tw-sticky tw-top-0">
              <tr>
                <th className="tw-px-3 tw-py-2 tw-text-left tw-font-medium tw-text-gray-600">Name</th>
                <th className="tw-px-3 tw-py-2 tw-text-left tw-font-medium tw-text-gray-600">National ID/Passport No.</th>
                <th className="tw-px-3 tw-py-2 tw-text-left tw-font-medium tw-text-gray-600">Email</th>
                <th className="tw-px-3 tw-py-2 tw-text-left tw-font-medium tw-text-gray-600">Send Activation Email</th>
                <th className="tw-px-3 tw-py-2 tw-text-left tw-font-medium tw-text-gray-600">Department</th>
                {hasActionCol && <th className="tw-px-3 tw-py-2 tw-text-left tw-font-medium tw-text-gray-600">Action</th>}
                {hasStatusCol && <th className="tw-px-3 tw-py-2 tw-text-left tw-font-medium tw-text-gray-600">Status</th>}
                {hasErrorCol && <th className="tw-px-3 tw-py-2 tw-text-left tw-font-medium tw-text-gray-600 tw-min-w-[160px]">Error</th>}
              </tr>
            </thead>
            <tbody className="tw-divide-y tw-divide-gray-100">
              {employees.map((emp, i) => (
                <tr key={i} className={cn(hasErrorCol && emp?.status !== 'OK' ? 'tw-bg-red-50' : '')}>
                  <td className="tw-px-3 tw-py-2 tw-text-gray-800">{emp.firstName} {emp.lastName}</td>
                  <td className="tw-px-3 tw-py-2 tw-text-gray-600">{emp.idNo || 'N/A'} / {emp.passNo || 'N/A'}</td>
                  <td className="tw-px-3 tw-py-2 tw-text-gray-600">{emp.email}</td>
                  <td className="tw-px-3 tw-py-2 tw-text-gray-600">{emp.sendActivationEmail ? 'Yes' : 'No'}</td>
                  <td className="tw-px-3 tw-py-2 tw-text-gray-600">{emp.departments?.[0] || ''}</td>
                  {hasActionCol && <td className="tw-px-3 tw-py-2 tw-text-gray-600">{emp.action}</td>}
                  {hasStatusCol && (
                    <td className="tw-px-3 tw-py-2">
                      {emp.status === 'OK'
                        ? <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-emerald-600"><CheckCircle className="tw-w-3 tw-h-3" /> OK</span>
                        : <span className="tw-text-red-500">{emp.status}</span>}
                    </td>
                  )}
                  {hasErrorCol && <td className="tw-px-3 tw-py-2 tw-text-red-500">{emp.error}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Footer */}
        <DialogFooter className="tw-pt-2">
          <button type="button" onClick={isSaved ? onClose : clearFile}
            className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-transparent tw-border tw-border-gray-300 tw-rounded-lg tw-cursor-pointer hover:tw-bg-gray-50">
            {isSaved ? 'Close' : 'Cancel'}
          </button>
          {!isSaved && (
            <button type="button" onClick={processImport} disabled={isSaving}
              className="tw-flex tw-items-center tw-gap-2 tw-px-5 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-indigo-600 tw-border-0 tw-rounded-lg tw-cursor-pointer hover:tw-bg-indigo-700 disabled:tw-opacity-50 tw-transition-colors">
              <Send className="tw-w-4 tw-h-4" />
              {isSaving ? 'Processing...' : 'Process Import'}
            </button>
          )}
        </DialogFooter>
      </div>
    );
  }

  return (
    <div className="tw-space-y-4">
      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        className={cn(
          'tw-border-2 tw-border-dashed tw-rounded-xl tw-py-8 tw-flex tw-flex-col tw-items-center tw-justify-center tw-cursor-pointer tw-transition-colors',
          isDragOver ? 'tw-border-indigo-400 tw-bg-indigo-50' : 'tw-border-gray-300 tw-bg-gray-50 hover:tw-border-gray-400'
        )}
      >
        <input ref={fileInputRef} type="file" accept=".csv" onChange={(e) => handleFile(e.target.files?.[0])} className="tw-hidden" />
        <FileText className={cn('tw-w-10 tw-h-10 tw-mb-2', isDragOver ? 'tw-text-indigo-500' : 'tw-text-indigo-400')} />
        <p className="tw-text-sm tw-font-medium tw-text-gray-700 tw-m-0">Drag CSV file here or click to browse</p>
        <p className="tw-text-xs tw-text-gray-400 tw-mt-1 tw-m-0">Max {MAX_RECORDS} records per upload</p>
      </div>

      {/* Template download card */}
      <div className="tw-flex tw-items-center tw-justify-between tw-border tw-border-gray-200 tw-rounded-lg tw-px-4 tw-py-3">
        <div className="tw-flex tw-items-center tw-gap-3">
          <FileText className="tw-w-8 tw-h-8 tw-text-emerald-600" />
          <div>
            <p className="tw-text-sm tw-font-medium tw-text-gray-800 tw-m-0">Standard Template.csv</p>
            <p className="tw-text-xs tw-text-gray-400 tw-m-0">Use this format for bulk imports</p>
          </div>
        </div>
        <button type="button" onClick={downloadTemplate} disabled={isDownloading}
          className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-medium tw-text-indigo-600 tw-bg-transparent tw-border-0 tw-cursor-pointer hover:tw-text-indigo-800 disabled:tw-opacity-50">
          <Download className="tw-w-4 tw-h-4" />
          {isDownloading ? 'Downloading...' : 'Download'}
        </button>
      </div>

      {/* CSV Requirements */}
      <div>
        <p className="tw-text-sm tw-font-medium tw-text-gray-700 tw-m-0 tw-mb-1.5">CSV Requirements:</p>
        <ul className="tw-list-disc tw-pl-5 tw-m-0 tw-space-y-0.5">
          <li className="tw-text-xs tw-text-gray-500">Headers: Email, FirstName, LastName, Designation, Department</li>
          <li className="tw-text-xs tw-text-gray-500">Emails must be unique</li>
          <li className="tw-text-xs tw-text-gray-500">Designation must match system values</li>
        </ul>
      </div>

      {/* Footer */}
      <DialogFooter className="tw-pt-2">
        <button type="button" onClick={onClose}
          className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-transparent tw-border tw-border-gray-300 tw-rounded-lg tw-cursor-pointer hover:tw-bg-gray-50">
          Cancel
        </button>
        <button type="button" disabled
          className="tw-flex tw-items-center tw-gap-2 tw-px-5 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-indigo-600 tw-border-0 tw-rounded-lg disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors">
          <Send className="tw-w-4 tw-h-4" /> Process Import
        </button>
      </DialogFooter>
    </div>
  );
};

export default CrewBulkImportTab;
